import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Stack, useMediaQuery } from '@mui/material'
import { H1, Body } from 'components/styledFonts'
import Block from 'components/Block'
import { useFormContext } from 'react-hook-form'
import {
    CharacterLimitField,
    DatePicker,
    ImageCaption,
    LabeledInputField,
    RadioGroupBase,
    Required,
    TextField,
} from 'components/Inputs'
import { SelectMovie, SelectPDF } from 'components/FileUploader'
import { maxLength } from './NewsLetterSubmissionBase'
import InquiryUserInput from 'components/InquiryUserInput'
import { useTemplateByNo } from 'hooks/template'
import TemplateTitleEdit from 'components/TemplateTitleEdit'
import FeedbackCheckbox from 'components/FeedbackCheckbox'
import GenreSelect from 'components/GenreSelect'
import CategorySelect from 'components/CategorySelect'
import PostButtonSet from 'components/PostButtonSet'
import { useNewsLetterByNo } from 'hooks/newsletter'

const NewsLetterPdfSubmission = () => {
    const { control, getValues,  setValue, handleSubmit, watch, saveDraft } = useFormContext()
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('mobile'))
    const { themeId, no } = useParams()
    const { data : template } = useTemplateByNo(themeId || watch("ネタテーマNo"))
    const { isFetching } = useNewsLetterByNo(no)

    // ネタテーマ取得時反映処理
    useEffect(() => {
        if (!template) {
            setValue("入稿方法", "PDF入稿")
            return
        }
        setValue("入稿方法", "PDF入稿（テンプレートから遷移）")
        setValue("ネタテーマNo", getValues("ネタテーマNo") || themeId)
    }, [template, themeId, getValues, setValue])

    return (
        <Stack
            gap={2}
        >
            <H1>{no ? "ニュースレターの下書きを編集" : `${themeId ? "テーマを用いて" : ""}ニュースレターを投稿`}</H1>
            <Block>
                <Stack
                    component="form"
                    gap={4}
                    pb={2}
                    sx={{ 
                        pl : { mobile : 2, xs : 0 },
                        pr : { mobile : 8, xs : 0 },
                        wordBreak : "break-word",
                    }}
                >
                    {template &&
                    <TemplateTitleEdit template={template} />
                    }
                    <Body><Required />がついている項目は必須です</Body>
                    <CharacterLimitField
                        label="タイトル"
                        name="ネタたねタイトル"
                        limit={maxLength.タイトル}
                        control={control}
                        required
                        multiline
                        disableEnter
                        watch={watch('ネタたねタイトル')}
                    />
                    <LabeledInputField
                        label="メイン画像"
                        note="※一覧ページでサムネイルとしても表示されます。画像は最大5MBまでアップロード可能です。"
                        required
                    >
                        <ImageCaption
                            name="メインイメージ"
                            captionName="メインイメージキャプション"
                            pict={watch('メインイメージ')}
                            control={control}
                            setValue={setValue}
                        />
                    </LabeledInputField>
                    <LabeledInputField
                        label="PDF"
                        required
                    >
                        <SelectPDF
                            name="PDF"
                            label="PDF"
                            file={watch('PDF')}
                            removable={watch('PDF')}
                            control={control}
                            setValue={setValue}
                            showName
                        />
                    </LabeledInputField>
                    <LabeledInputField
                        label="動画"
                        note="※動画は最大50MBまでアップロード可能です。"
                    >
                        <RadioGroupBase
                            value={watch(`アップロード方法`)}
                            onChange={e => setValue(`アップロード方法`, e.target.value)}
                            row={!isMobile}
                            sx={{
                                columnGap : 0,
                            }}
                            list={[
                                { label : '動画をアップロード', value : '動画をアップロード', disabled : !!watch(`動画URL`) },
                                { label : '動画の埋め込みタグを入力', value : '動画の埋め込みタグを入力', disabled : !!(watch(`動画`)?.url || watch(`動画`)?.blob) }
                            ]}
                        />
                        {watch(`アップロード方法`) === "動画をアップロード" &&
                        <SelectMovie
                            name={`動画`}
                            file={watch(`動画`)}
                            control={control}
                            setValue={setValue}
                            showName
                            removable
                        />      
                        }
                        {watch(`アップロード方法`) === "動画の埋め込みタグを入力" && <>
                        <TextField
                            name={`動画URL`}
                            control={control}
                            watch={watch(`動画URL`)}
                            placeholder="YouTube動画の埋め込みタグを貼り付けてください。"
                        />
                        <Body>{"YouTube動画の「共有メニュー > 埋め込む」から取得できる、埋め込み用タグをコピーアンドペーストしてください。"}</Body>
                        </>}
                    </LabeledInputField>
                    <CharacterLimitField
                        label="検索キーワード"
                        name="検索キーワード"
                        limit={maxLength.検索キーワード}
                        control={control}
                        required
                        disableEnter
                        watch={watch('検索キーワード')}
                        note="※ニュースレターの内容に関連あるワードのみ半角スペース区切りで入力してください"
                    />
                    <InquiryUserInput
                        control={control}
                        setValue={setValue}
                        note="企業情報設定と異なる方を問い合わせ担当者にする際は入力してください"
                    />
                    <LabeledInputField
                        label="投稿日時"
                    >
                        <DatePicker
                            name="公開日"
                            control={control}
                            showTimeInput
                        />
                    </LabeledInputField>
                    <GenreSelect control={control} />
                    <CategorySelect control={control} />
                    <FeedbackCheckbox
                        name={"フィードバック依頼"}
                        disabled={!!watch("フィードバックNo")}
                        control={control}
                    />
                </Stack>
            </Block>
            <PostButtonSet
                handleSubmit={handleSubmit}
                watch={watch}
                saveDraft={saveDraft}
                isFeedback={watch("フィードバック依頼") && !watch("フィードバックNo")}
                isFetching={isFetching}
            />
        </Stack>
    )
}

export default NewsLetterPdfSubmission
